import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Button, CircleLoader, FormFieldSet, Modal } from "..";

/**
 * @typedef {Object} EmployeeRow
 * @property {string}  id
 * @property {string}  name
 * @property {number}  [candidates]
 * @property {number}  [submissions]
 * @property {number}  [interviews]
 * @property {number}  [placements]
 */

/**
 * @typedef {Object} ColumnHeader
 * @property {string} label  - Display label
 * @property {string} key    - Data key on EmployeeRow
 */

/**
 * @typedef {Object} LoadingStates
 * @property {boolean} [items]
 * @property {boolean} [subItems]
 * @property {boolean} [modal]
 */

/**
 * @typedef {Object} DailyProductionWidgetProps
 * @property {EmployeeRow[]}   items              - Flat list of primary items already loaded
 * @property {EmployeeRow[]}   subItems           - Flat list of secondary items for selected primary item
 * @property {ColumnHeader[]}  [columnHeaders]       - Metric columns to display
 * @property {boolean}         [hasMoreItems]     - Whether more items can be loaded
 * @property {boolean}         [hasMoreSubItems]   - Whether more subItems can be loaded
 * @property {LoadingStates}   [loadingStates]       - Granular loading flags
 * @property {string|null}     [selectedItemId]   - Controlled selected item
 * @property {Function}        onItemSelect       - (item: EmployeeRow) => void
 * @property {Function}        onLoadMoreItems    - () => void
 * @property {Function}        onLoadMoreSubItems  - (itemId: string) => void
 * @property {Function}        [onMetricClick]       - (entity: EmployeeRow, header: ColumnHeader) => void
 * @property {React.ReactNode} [modalContent]        - Rendered inside the detail modal
 * @property {string}          [modalTitle]          - Detail modal title (controlled from outside)
 * @property {boolean}         [isModalOpen]         - Controlled modal open state
 * @property {Function}        [onModalClose]        - () => void
 */

const LoadMoreButton = React.memo(
  ({ onClick, isLoading, label = "Load More" }) => (
    <div className="flex justify-center py-3">
      <Button
        onClick={onClick}
        disabled={isLoading}
        title={isLoading ? "Loading..." : label}
        variant="outlined"
      />
    </div>
  ),
);

const EmptyState = React.memo(({ message = "No data available" }) => (
  <div className="flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
    <span className="material-symbols-outlined text-4xl">inbox</span>
    <span className="text-sm">{message}</span>
  </div>
));

const GridRow = React.memo(
  ({
    employee,
    employeeType = "member", // "info" | "success" | "member"
    columnHeaders,
    gridCols,
    isSelected = false,
    onRowClick,
    onMetricClick,
    clickableKeys,
  }) => {
    const styleMap = {
      info: "bg-info-500 text-white",
      success: "bg-success-100 text-black",
      member: "bg-white text-black",
    };

    const fixedCellBase =
      "px-3 py-1 font-medium text-sm h-[44px] border border-gray-300 flex items-center border-r-0 border-b-0 sticky left-0 z-20";
    const dataCellBase =
      "px-3 py-1 font-normal text-sm h-[44px] border border-gray-300 flex items-center justify-center border-r-0 border-b-0 bg-white text-black";

    const colorClass = styleMap[employeeType] ?? styleMap.member;
    const selectedRing = isSelected ? "ring-2 ring-inset ring-info-300" : "";

    return (
      <div className="grid" style={{ gridTemplateColumns: gridCols }}>
        {/* Name cell */}
        <div
          className={`${fixedCellBase} ${colorClass} ${selectedRing} ${
            employeeType === "info" ? "cursor-pointer select-none" : ""
          } flex justify-between gap-1`}
          onClick={employeeType === "info" ? onRowClick : undefined}
          title={employee?.name}
        >
          <span className="line-clamp-1 flex-1">{employee?.name ?? "—"}</span>
          {employeeType === "info" && (
            <span className="material-symbols-outlined text-base leading-none opacity-80">
              {isSelected ? "expand_less" : "expand_more"}
            </span>
          )}
        </div>

        {/* Metric cells */}
        {columnHeaders.map((header, idx) => {
          const value = employee?.[header.key] ?? 0;
          const canClick =
            clickableKeys.has(header.key) &&
            value > 0 &&
            employeeType !== "notAssigned";
          return (
            <div
              key={idx}
              className={`${dataCellBase} font-medium ${
                canClick
                  ? "cursor-pointer hover:text-primary-600 transition-colors"
                  : ""
              }`}
              onClick={
                canClick ? () => onMetricClick?.(employee, header) : undefined
              }
            >
              {value}
            </div>
          );
        })}
      </div>
    );
  },
);

const ExpandedSubItems = React.memo(
  ({
    itemId,
    subItems = [],
    hasMore = false,
    isLoading = false,
    columnHeaders,
    gridCols,
    onMetricClick,
    clickableKeys,
    onLoadMore,
  }) => {
    return (
      <div className="mb-2 max-h-[320px] overflow-y-auto border-b border-gray-200 bg-gray-50/30">
        {isLoading && subItems.length === 0 ? (
          <div className="flex items-center justify-center py-2">
            <CircleLoader size={20} />
          </div>
        ) : subItems.length === 0 ? (
          <div className="px-6 py-2 text-xs text-gray-400 italic">
            No secondary items found.
          </div>
        ) : (
          subItems.map((subItem) => (
            <div key={subItem.id} className="shadow-sm">
              <GridRow
                employee={subItem}
                employeeType="success"
                columnHeaders={columnHeaders}
                gridCols={gridCols}
                onMetricClick={onMetricClick}
                clickableKeys={clickableKeys}
              />
            </div>
          ))
        )}

        {/* Load More Button for sub-items */}
        {hasMore && (
          <LoadMoreButton
            onClick={() => onLoadMore?.(itemId)}
            isLoading={isLoading}
            label="Load More Sub-Items"
          />
        )}
      </div>
    );
  },
);

/** Sticky table header */
const TableHeader = React.memo(({ columnHeaders, gridCols }) => (
  <div className="sticky top-0 z-40 bg-white shadow-sm mb-2">
    <div className="grid" style={{ gridTemplateColumns: gridCols }}>
      <div className="px-3 py-1 font-normal text-sm h-[54px] border border-gray-300 flex items-center border-r-0 text-gray-500 bg-gray-100 sticky left-0 z-30">
        Name
      </div>
      {columnHeaders.map((header, idx) => (
        <div
          key={idx}
          className="px-3 py-1 font-normal text-sm h-[54px] border border-gray-300 flex items-center border-r-0 text-gray-500 bg-gray-100"
        >
          {header.label}
        </div>
      ))}
    </div>
  </div>
));

/** Sticky footer totals */
const TableFooter = React.memo(({ columnHeaders, gridCols, grandTotals }) => (
  <div className="sticky bottom-0 z-30 bg-white shadow-sm">
    <div className="grid" style={{ gridTemplateColumns: gridCols }}>
      <div className="px-3 py-1 font-bold text-sm h-[44px] border border-gray-300 flex items-center border-r-0 border-b-0 text-gray-100 bg-primary-600 sticky left-0 z-30">
        Total Count
      </div>
      {columnHeaders.map((header, idx) => (
        <div
          key={idx}
          className="px-3 py-1 font-bold text-sm h-[44px] border border-gray-300 flex items-center justify-center border-r-0 border-b-0 text-gray-800 bg-primary-50"
        >
          {grandTotals[header.key] ?? 0}
        </div>
      ))}
    </div>
  </div>
));

/** Inline modal */
const DetailModal = React.memo(({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    if (isOpen) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} width="max-w-6xl" titleColor={"primary"}>
      <div className="flex-1 overflow-auto p-4">{children}</div>
    </Modal>
  );
});

const DailyProductionWidget = ({
  // Data
  items = [],
  subItems = {}, // Object mapping itemId to array of subItems
  columnHeaders = [
    { label: "Candidates", key: "candidates" },
    { label: "Submissions", key: "submissions" },
    { label: "Interviews", key: "interviews" },
    { label: "Placements", key: "placements" },
  ],
  // Pagination
  hasMoreItems = false,
  hasMoreSubItems = {}, // Object mapping itemId to boolean
  // Loading
  loadingStates = {}, // { items: boolean, subItems: Record<string, boolean>, modal: boolean }
  // Selection (controlled)
  expandedItemIds = [],
  // Callbacks
  onItemToggle,
  onLoadMoreItems,
  onLoadMoreSubItems,
  onMetricClick,
  // Modal (controlled from outside)
  modalContent = null,
  modalTitle = "",
  isModalOpen = false,
  onModalClose,
}) => {
  // ── refs for IntersectionObserver sentinel elements ──
  const [selectedUsers, setSelectedUsers] = useState([]);

  // ── Grid column template ──
  const gridCols = useMemo(
    () =>
      `minmax(200px, 1fr) repeat(${columnHeaders.length}, minmax(96px, 1fr))`,
    [columnHeaders.length],
  );

  // ── Set of metric keys that are clickable ──
  const clickableKeys = useMemo(
    () => new Set(["candidates", "submissions", "interviews", "placements"]),
    [],
  );

  const visibleItems = useMemo(() => {
    if (selectedUsers.length === 0) return items;
    return items.filter((m) => selectedUsers.includes(m.id));
  }, [items, selectedUsers]);

  // ── Grand totals: sum items + visible subItems ──
  const grandTotals = useMemo(() => {
    const totals = {};
    columnHeaders.forEach((h) => {
      totals[h.key] = 0;
    });

    visibleItems.forEach((m) => {
      columnHeaders.forEach((h) => {
        totals[h.key] += m[h.key] ?? 0;
      });
    });

    // Add subItems totals (only if they belong to the selected item)
    expandedItemIds.forEach((id) => {
      const children = subItems[id] || [];
      children.forEach((r) => {
        columnHeaders.forEach((h) => {
          totals[h.key] += r[h.key] ?? 0;
        });
      });
    });

    return totals;
  }, [visibleItems, subItems, columnHeaders, expandedItemIds]);

  // ── Memoised callbacks ──
  const handleMetricClick = useCallback(
    (entity, header) => onMetricClick?.(entity, header),
    [onMetricClick],
  );

  // ── Filter States ──
  const userOptions = useMemo(() => {
    return items.map((m) => ({ label: m.name, value: m.id }));
  }, [items]);

  const isEmpty = visibleItems.length === 0 && !loadingStates.items;

  return (
    <div className="w-full h-full flex flex-col gap-2 overflow-hidden bg-gray-50 p-4">
      {/* Toolbar */}
      <div className="flex items-center bg-white p-3 rounded border border-gray-200 shadow-sm flex-shrink-0">
        <div className="w-72">
          <FormFieldSet
            type="multiSelect"
            noTitle
            options={userOptions}
            value={selectedUsers}
            onChange={(val) => setSelectedUsers(val)}
            size="sm"
            placeholder="Select items..."
            minwidth="min-w-48"
            showSelectAll={false}
            showSearch
          />
        </div>
      </div>

      {/* Table container */}
      <div className="flex-1 min-h-0 overflow-auto bg-white rounded border border-gray-200">
        <div className="flex flex-col w-full relative min-w-max">
          <TableHeader columnHeaders={columnHeaders} gridCols={gridCols} />

          {/* Scrollable body */}
          <div className="flex-1">
            {isEmpty ? (
              <EmptyState message="No data available" />
            ) : (
              <>
                {/* ── Primary rows ── */}
                {visibleItems.map((item) => {
                  const isExpanded = expandedItemIds.includes(item.id);
                  return (
                    <React.Fragment key={item.id}>
                      <div className="mb-1 shadow-sm">
                        <GridRow
                          employee={item}
                          employeeType="info"
                          columnHeaders={columnHeaders}
                          gridCols={gridCols}
                          isSelected={isExpanded}
                          onRowClick={() => onItemToggle?.(item)}
                          onMetricClick={handleMetricClick}
                          clickableKeys={clickableKeys}
                        />
                      </div>

                      {/* ── Secondary sub-rows (expanded) ── */}
                      {isExpanded && (
                        <ExpandedSubItems
                          itemId={item.id}
                          subItems={subItems[item.id]}
                          hasMore={hasMoreSubItems[item.id]}
                          isLoading={loadingStates.subItems?.[item.id]}
                          columnHeaders={columnHeaders}
                          gridCols={gridCols}
                          onMetricClick={handleMetricClick}
                          clickableKeys={clickableKeys}
                          onLoadMore={onLoadMoreSubItems}
                        />
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Load More Button for primary items */}
                {hasMoreItems && selectedUsers.length === 0 && (
                  <LoadMoreButton
                    onClick={onLoadMoreItems}
                    isLoading={loadingStates.items}
                    label="Load More Items"
                  />
                )}
              </>
            )}
          </div>

          <TableFooter
            columnHeaders={columnHeaders}
            gridCols={gridCols}
            grandTotals={grandTotals}
          />
        </div>
      </div>

      {/* Detail modal — content is injected from the parent */}
      <DetailModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        title={modalTitle}
      >
        {modalContent ?? (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
            No detail content provided.
          </div>
        )}
      </DetailModal>
    </div>
  );
};

export default React.memo(DailyProductionWidget);
